import { useEffect, useRef, useState } from "react";
import { uploadAsset, type ManifestEntry } from "../lib/contentApi";
import { assembleStripBlob, fileToImage, normalizeFrame, sliceStrip } from "../lib/strip";

// 캐릭터 인라인 '움직임(애니)' 만들기 — 그림 여러 장 → 스트립 자동 조립 → 게시.
// 메인 화면 캐릭터 카드 안에 넣어, 그림과 움직임을 '한 자리'에서 관리한다(탭 이동·슬롯키 입력 불필요).
// 전체 애니 탭(AnimationStudio)과 같은 규격(lib/strip)을 쓴다.

interface Frame {
  id: number;
  img: HTMLImageElement;
}

/** 스트립 미리보기(배경 이동 — 캔버스 오염 없음). */
function Play({ url, fps }: { url: string; fps: number }) {
  const [n, setN] = useState(1);
  const [i, setI] = useState(0);
  useEffect(() => {
    let alive = true;
    const img = new Image();
    img.onload = () => alive && setN(Math.max(1, Math.round(img.width / img.height)));
    img.src = url;
    return () => {
      alive = false;
    };
  }, [url]);
  useEffect(() => {
    if (n < 2) return;
    const t = window.setInterval(() => setI((v) => (v + 1) % n), 1000 / Math.max(0.2, fps));
    return () => window.clearInterval(t);
  }, [n, fps]);
  return (
    <span
      className="sb__play"
      style={{
        backgroundImage: `url(${url})`,
        backgroundSize: `${Math.max(1, n) * 100}% 100%`,
        backgroundPosition: n > 1 ? `${(i / (n - 1)) * 100}% 0` : "0 0",
      }}
    />
  );
}

export function StripBuilder({
  slotKey,
  entry,
  onChange,
}: {
  slotKey: string; // 예: landing.char.greg.strip
  entry?: ManifestEntry; // 현재 움직임(로컬 매니페스트 — 미게시분 포함)
  onChange: (key: string, entry: ManifestEntry | null) => void; // 다른 항목과 동일: 로컬 반영 → 상단 [게시]
}) {
  const [open, setOpen] = useState(false);
  const [frames, setFrames] = useState<Frame[]>([]);
  const [fps, setFps] = useState(entry?.fps ?? 2);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [playIdx, setPlayIdx] = useState(0);
  const nextId = useRef(1);
  const previewRef = useRef<HTMLCanvasElement>(null);

  // 미리보기 재생
  useEffect(() => {
    if (frames.length < 2) return;
    const t = window.setInterval(() => setPlayIdx((v) => (v + 1) % frames.length), 1000 / Math.max(0.2, fps));
    return () => window.clearInterval(t);
  }, [frames.length, fps]);
  useEffect(() => {
    const cv = previewRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d")!;
    ctx.clearRect(0, 0, cv.width, cv.height);
    const f = frames[Math.min(playIdx, frames.length - 1)];
    if (f) ctx.drawImage(normalizeFrame(f.img), 0, 0, cv.width, cv.height);
  }, [frames, playIdx]);

  async function addFiles(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      try {
        const img = await fileToImage(file);
        setFrames((p) => [...p, { id: nextId.current++, img }]);
      } catch {
        setMsg(`${file.name}: 이미지를 읽을 수 없어요`);
      }
    }
  }

  function move(id: number, dir: -1 | 1) {
    setFrames((p) => {
      const i = p.findIndex((f) => f.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= p.length) return p;
      const n = [...p];
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });
  }

  /** 게시된 움직임을 프레임으로 되불러와 수정 시작. */
  async function editExisting() {
    if (!entry?.url) return;
    setBusy(true);
    setMsg("불러오는 중…");
    try {
      const imgs = await sliceStrip(entry.url);
      setFrames(imgs.map((img) => ({ id: nextId.current++, img })));
      setFps(entry.fps ?? 2);
      setOpen(true);
      setMsg(`${imgs.length}장을 불러왔어요. 고친 뒤 '움직임 저장'을 누르세요.`);
    } catch (e) {
      setMsg(`불러오기 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (frames.length < 2) {
      setMsg("그림을 2장 이상 올려주세요 (2장이면 왔다갔다 움직여요)");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const blob = await assembleStripBlob(frames.map((f) => f.img));
      const url = await uploadAsset(slotKey, new File([blob], "strip.webp", { type: "image/webp" }));
      // 다른 항목과 동일: 로컬에 담고, 상단 [게시]로 웹 반영(미게시 이미지 편집이 유실되지 않게).
      onChange(slotKey, { ...(entry ?? {}), url, fps });
      setMsg("✅ 움직임을 담았어요 — 위의 [🚀 게시]를 눌러야 웹에 나와요");
      setFrames([]);
      setOpen(false);
    } catch (e) {
      setMsg(`❌ 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  function clearAnim() {
    if (!entry) return;
    if (!window.confirm("이 캐릭터의 움직임을 지울까요? (정지 그림으로 돌아가요)")) return;
    onChange(slotKey, null);
    setMsg("움직임을 뺐어요 — 위의 [🚀 게시]를 눌러야 웹에 반영돼요");
  }

  return (
    <div className="sb">
      <div className="sb__head">
        <span className="sb__title">🎞️ 움직임</span>
        {entry ? (
          <span className="sb__has">
            <Play url={entry.url} fps={entry.fps ?? 2} />
            <span className="sb__has-txt">지금 움직이는 중 · {entry.fps ?? 2}단계</span>
          </span>
        ) : (
          <span className="sb__none">아직 없음 (정지 그림)</span>
        )}
      </div>

      {!open ? (
        <div className="sb__actions">
          <button type="button" className="sb__btn" onClick={() => setOpen(true)} disabled={busy}>
            {entry ? "새로 만들기" : "＋ 움직임 만들기"}
          </button>
          {entry ? (
            <>
              <button type="button" className="sb__btn" onClick={editExisting} disabled={busy}>지금 것 고치기</button>
              <button type="button" className="sb__btn sb__btn--del" onClick={clearAnim} disabled={busy}>지우기</button>
            </>
          ) : null}
        </div>
      ) : (
        <div className="sb__builder">
          <p className="sb__hint">캐릭터 그림을 <b>2장 이상</b> 올리세요. 예: 눈뜬 그림 + 눈감은 그림 → 깜빡여요. (투명 배경 권장)</p>
          <label className="sb__drop">
            <input type="file" accept="image/*" multiple onChange={(e) => void addFiles(e.target.files)} />
            <span>📁 그림 여러 장 고르기</span>
          </label>

          {frames.length > 0 ? (
            <div className="sb__frames">
              {frames.map((f, i) => (
                <div key={f.id} className="sb__frame">
                  <img src={f.img.src} alt={`장면 ${i + 1}`} />
                  <span className="sb__frame-no">{i + 1}</span>
                  <div className="sb__frame-ops">
                    <button type="button" onClick={() => move(f.id, -1)} disabled={i === 0} title="앞으로">◀</button>
                    <button type="button" onClick={() => setFrames((p) => p.filter((x) => x.id !== f.id))} title="빼기">✕</button>
                    <button type="button" onClick={() => move(f.id, 1)} disabled={i === frames.length - 1} title="뒤로">▶️</button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {frames.length >= 2 ? (
            <div className="sb__preview">
              <canvas ref={previewRef} width={120} height={120} />
              <label className="sb__speed">
                속도 <span className="sb__speed-txt">느리게</span>
                <input type="range" min={1} max={3} step={0.5} value={fps} onChange={(e) => setFps(Number(e.target.value))} />
                <span className="sb__speed-txt">빠르게</span>
              </label>
            </div>
          ) : null}

          <div className="sb__actions">
            <button
              type="button"
              className="sb__btn sb__btn--go"
              onClick={save}
              disabled={busy || frames.length < 2}
              // 🔴 한 장만 담으면 회색인데 이유가 어디에도 없었다.
              title={frames.length < 2 ? "그림이 2장 이상이어야 움직여요." : undefined}
            >
              {busy ? "저장 중…" : "움직임 저장"}
            </button>
            <button type="button" className="sb__btn" onClick={() => { setOpen(false); setFrames([]); setMsg(null); }} disabled={busy}>
              닫기
            </button>
          </div>
        </div>
      )}

      {msg ? <p className="msg">{msg}</p> : null}
    </div>
  );
}
